import { useEffect, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useAuth } from '@/stores/auth';
import { useConnection } from '@/stores/connection';
import { DEFAULT_COMPANY, useCompany } from '@/stores/company';
import { LoginBrand } from './login/LoginBrand';

/**
 * Sign-in, and — for someone with two-step sign-in on — the code from their
 * phone. Every failed sign-in shows the server's one message, which does not
 * say whether a user ID exists or an account is locked (.docs/019).
 */
export default function Login() {
  const { signIn, signingIn, error, notice, twoStepChallenge } = useAuth();
  const company = useCompany((s) => s.company);
  const publicLoaded = useCompany((s) => s.publicLoaded);
  const online = useConnection((s) => s.online);

  // Name, logo and picture from Settings › Company, so staff recognise their
  // own company's sign-in page. Anyone may read these; nothing else is shown.
  useEffect(() => {
    void useCompany.getState().loadPublic();
  }, []);
  const customName = company.nameEn !== DEFAULT_COMPANY.nameEn ? company.nameEn : null;

  return (
    <main className="login">
      <div className="login__card">
        <LoginBrand
          // Known from this machine's last visit, or once the server answers;
          // until then neither picture, so the built-in one never flashes up
          // before the company's own.
          picture={company.loginPicture === undefined && !publicLoaded ? 'pending' : (company.loginPicture ?? null)}
          name={customName}
          logoDataUrl={company.logoDataUrl}
        />

        <section className="login__form">
          {twoStepChallenge ? (
            <TwoStepForm />
          ) : (
            <PasswordForm signIn={signIn} signingIn={signingIn} />
          )}

          {!online && (
            // A cold start with no network lands here. Somebody who has signed
            // in on this PC in the last week can still get in, against the
            // copy of their work kept here (.docs/023).
            <p className="login__notice" role="status">
              <span aria-hidden="true">⚠ </span>
              No connection. If you signed in on this PC in the last 7 days, you can still sign in with your
              password.
            </p>
          )}

          {notice && !error && (
            <p className="login__notice" role="status">
              <span aria-hidden="true">ⓘ </span>
              {notice}
            </p>
          )}

          {error && (
            // Icon and text, never colour alone.
            <p className="login__error" role="alert">
              <span aria-hidden="true">⚠ </span>
              {error}
            </p>
          )}

          <p className="login__vendor">A SalixSoft LLC product</p>
        </section>
      </div>
    </main>
  );
}

function PasswordForm({
  signIn,
  signingIn,
}: {
  signIn: (identifier: string, password: string) => Promise<void>;
  signingIn: boolean;
}) {
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [capsOn, setCapsOn] = useState(false);

  function submit(e: FormEvent) {
    e.preventDefault();
    void signIn(identifier.trim(), password);
  }

  // Capital and small letters matter in a password, and a stuck Caps Lock is
  // the commonest reason a right password is refused.
  function watchCaps(e: KeyboardEvent<HTMLInputElement>) {
    setCapsOn(e.getModifierState?.('CapsLock') ?? false);
  }

  return (
    <>
      <div>
        <h1 className="login__title">Sign in</h1>
        <p className="login__subtitle">Use your company user ID or mobile number.</p>
      </div>

      <form onSubmit={submit} className="login__fields">
        <label className="field">
          <span className="field__label">User ID or mobile</span>
          <input
            className="field__input"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            autoComplete="username"
            autoCapitalize="none"
            spellCheck={false}
            autoFocus
            required
          />
        </label>

        <label className="field">
          <span className="field__label">Password</span>
          <span className="login__password">
            <input
              className="field__input"
              type={show ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyDown={watchCaps}
              onKeyUp={watchCaps}
              autoComplete="current-password"
              required
            />
            <button
              type="button"
              className="btn btn--ghost login__show"
              onClick={() => setShow((v) => !v)}
              aria-pressed={show}
            >
              {show ? 'Hide' : 'Show'}
            </button>
          </span>
        </label>

        {capsOn && (
          <p className="login__notice" role="status">
            <span aria-hidden="true">⇪ </span>
            Caps Lock is on.
          </p>
        )}

        <button type="submit" className="btn btn--primary" disabled={signingIn}>
          {signingIn ? 'Signing in…' : 'Sign in'}
        </button>

        <p className="login__hint">
          Forgotten your password? Ask an administrator to reset it.
        </p>
      </form>
    </>
  );
}

function TwoStepForm() {
  const { finishTwoStep, cancelTwoStep, signingIn } = useAuth();
  const [code, setCode] = useState('');
  const [useBackup, setUseBackup] = useState(false);

  function submit(e: FormEvent) {
    e.preventDefault();
    void finishTwoStep(code.trim());
  }

  return (
    <>
      <div>
        <h1 className="login__title">Two-step sign-in</h1>
        <p className="login__subtitle">
          {useBackup
            ? 'Type one of the backup codes you were given when you switched two-step on. Each works once.'
            : 'Open the authenticator app on your phone and type the 6-digit code it shows.'}
        </p>
      </div>

      <form onSubmit={submit} className="login__fields">
        <label className="field">
          <span className="field__label">{useBackup ? 'Backup code' : '6-digit code'}</span>
          <input
            className="field__input login__code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="one-time-code"
            inputMode={useBackup ? 'text' : 'numeric'}
            placeholder={useBackup ? 'abcd-efgh' : '123456'}
            autoFocus
            required
          />
        </label>

        <button type="submit" className="btn btn--primary" disabled={signingIn}>
          {signingIn ? 'Checking…' : 'Sign in'}
        </button>

        <p className="login__hint">
          <button
            type="button"
            className="linkbutton"
            onClick={() => {
              setUseBackup((v) => !v);
              setCode('');
            }}
          >
            {useBackup ? 'Use the code from my phone instead' : 'Use a backup code instead'}
          </button>
          {' · '}
          <button type="button" className="linkbutton" onClick={cancelTwoStep}>
            Back
          </button>
        </p>
        <p className="login__hint">Lost your phone? Ask an administrator to reset two-step sign-in.</p>
      </form>
    </>
  );
}
